<template>
  <div class="tab-container">
    <div v-if="loading" class="onboarding-loading">
      <free-style-shimmer
          :is-loading="true"
          height="120px"
          width="100%"
          border-radius="16px"
          color="#EAECF0"
      />
      <free-style-shimmer
          :is-loading="true"
          height="80px"
          width="100%"
          border-radius="12px"
          color="#EAECF0"
      />
      <free-style-shimmer
          :is-loading="true"
          height="80px"
          width="100%"
          border-radius="12px"
          color="#EAECF0"
      />
    </div>

    <div v-else-if="!data || !data.onboarding" class="no-user-selected">
      {{ $t('no_onboarding_assigned') }}
    </div>

    <div v-else class="onboarding-wrapper">
      <h1 class="page-title">{{$t('onboarding_answers')}}</h1>
      <div class="onboarding-header">
        <div class="header-left">
          <h3>{{ data.onboarding.name }}</h3>
          <span class="onboarding-date">{{ data.onboarding.createdAt }}</span>
        </div>
        <div class="header-right">
          <div class="stat-badge">
            <span class="stat-value">{{ data.statistics.answered }}/{{ data.statistics.total }}</span>
            <span class="stat-label">{{ $t('answered') }}</span>
          </div>
          <div class="stat-badge completion">
            <span class="stat-value">%{{ data.statistics.completion_rate }}</span>
            <span class="stat-label">{{ $t('completion_rate') }}</span>
          </div>
        </div>
      </div>

      <div class="answers-section">
        <h3>{{ $t('questions') }}</h3>
        <div class="answers-list">
          <div
              v-for="(question, index) in data.questions"
              :key="question.questionId"
              class="answer-item"
          >
            <div class="question-text">
              <span class="question-id">{{ $t('question') }} {{ index + 1 }}: </span>
              {{ question.name }}
            </div>

            <div class="options-wrapper" v-if="question.content.options && question.content.options.length > 0">
              <div
                  v-for="option in question.content.options"
                  :key="option.id"
                  class="option-item"
                  :class="{ 'selected': isOptionSelected(question, option.id) }"
              >
                <div class="option-radio" :class="{ 'checked': isOptionSelected(question, option.id) }"></div>
                <span class="option-text">{{ getOptionText(option) }}</span>
              </div>
            </div>

            <div v-if="hasAnswer(question)" class="answer-text">
              <span>{{$t('answer_title')}} :</span>
              <template v-if="Array.isArray(question.answer)">
                <span v-for="(ans, ansIndex) in question.answer" :key="ansIndex" class="answer-badge">
                  {{ getAnswerDisplay(question, ans) }}
                </span>
              </template>
              <span v-else class="answer-badge">
                {{ getAnswerDisplay(question, question.answer) }}
              </span>
            </div>
            <div v-else class="answer-text not-answered">
              {{ $t('not_answered_yet') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OnboardingTab',
  props: {
    data: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getQuestionTypeLabel(type) {
      const types = {
        'single': this.$t('single_choice'),
        'multiple': this.$t('multiple_choice'),
        'text': this.$t('text_answer'),
        'textarea': this.$t('text_answer'),
        'rating': this.$t('rating'),
        'scale': this.$t('scale')
      };
      return types[type] || type;
    },

    hasAnswer(question) {
      if (!question.answer) return false;

      if (Array.isArray(question.answer)) {
        return question.answer.length > 0;
      }

      if (typeof question.answer === 'object') {
        return Object.keys(question.answer).length > 0;
      }

      if (typeof question.answer === 'string') {
        return question.answer.trim().length > 0;
      }

      return true;
    },

    isOptionSelected(question, optionId) {
      if (!question.answer) {
        return false;
      }

      if (Array.isArray(question.answer)) {
        if (question.answer.length === 0) return false;

        return question.answer.some(ans => {
          if (typeof ans === 'object' && ans !== null) {
            if (ans.answer !== undefined) {
              return ans.answer === optionId ||
                  ans.answer === String(optionId) ||
                  String(ans.answer) === String(optionId) ||
                  ans.answer === Number(optionId);
            }
            if (ans.id !== undefined) {
              return ans.id === optionId ||
                  String(ans.id) === String(optionId);
            }
          }
          return ans === optionId ||
              String(ans) === String(optionId) ||
              ans === Number(optionId);
        });
      }

      if (typeof question.answer === 'object') {
        if (question.answer.answer !== undefined) {
          return question.answer.answer === optionId ||
              String(question.answer.answer) === String(optionId);
        }
        return question.answer.id === optionId ||
            String(question.answer.id) === String(optionId);
      }

      return question.answer === optionId ||
          String(question.answer) === String(optionId) ||
          question.answer === Number(optionId);
    },

    getOptionText(option) {
      return option.name || option.text || option.value || option.label || `${this.$t('option')} ${option.id + 1}`;
    },

    getAnswerDisplay(question, answer) {
      if (question.content.type === 'text' || question.content.type === 'textarea') {
        if (typeof answer === 'object' && answer !== null && answer.answer !== undefined) {
          return answer.answer;
        }
        return answer;
      }

      if (typeof answer === 'object' && answer !== null) {
        if (answer.answer !== undefined) {
          const option = question.content.options.find(opt =>
              opt.id === answer.answer ||
              opt.id === String(answer.answer) ||
              String(opt.id) === String(answer.answer)
          );
          if (option) {
            return option.name || option.text || option.value || option.label;
          }
          return answer.answer;
        }

        if (answer.name || answer.text || answer.value) {
          return answer.name || answer.text || answer.value;
        }

        if (answer.id !== undefined) {
          const option = question.content.options.find(opt =>
              opt.id === answer.id || String(opt.id) === String(answer.id)
          );
          if (option) {
            return option.name || option.text || option.value || option.label;
          }
        }
      }

      const option = question.content.options.find(opt =>
          opt.id === answer ||
          opt.id === parseInt(answer) ||
          String(opt.id) === String(answer)
      );

      if (option) {
        return option.name || option.text || option.value || option.label || answer;
      }

      return answer;
    }
  }
}
</script>

<style scoped>
.tab-container {
  width: 100%;
}

.onboarding-loading {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.no-user-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #667085;
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
}

.onboarding-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-title{
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  color: #040C2D;
  padding: 0;
}

.onboarding-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: #FFFFFF;
  border: 1px solid #EAECF0;
  border-radius: 12px;
}

.header-left h3 {
  margin: 0;
  padding: 0;
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  color: #040C2D;
}

.onboarding-date {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #667085;
  margin-top: 4px;
  display: block;
}

.header-right {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.stat-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 16px;
  background-color: #F9FAFB;
  border-radius: 8px;
  min-width: 80px;
}

.stat-value {
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  color: #101828;
}

.stat-label {
  font-family: euclid_regular, sans-serif;
  font-size: 11px;
  color: #667085;
  margin-top: 2px;
}

.answers-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.answers-section h3 {
  margin: 0;
  padding: 0;
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  color: #040C2D;
}

.answers-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.answer-item {
  background-color: #FFFFFF;
  border: 1px solid #EAECF0;
  border-radius: 9px;
  padding: 15px;
}

.question-text {
  font-size: 14px;
  color: #667085;
  font-family: euclid_regular, sans-serif;
  margin-bottom: 12px;
}

.question-id {
  font-size: 14px;
  color: #101828;
  font-family: euclid_medium, sans-serif;
}

.options-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.option-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background-color: #F9FAFB;
  border-radius: 8px;
  border: 1px solid #EAECF0;
  transition: all 0.2s ease;
}

.option-radio {
  width: 16px;
  height: 16px;
  border: 2px solid #D0D5DD;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.option-radio.checked {
  border-color: #4A68F8;
  background-color: #4A68F8;
  box-shadow: inset 0 0 0 3px #FFFFFF;
}

.option-text {
  font-family: euclid_regular, sans-serif;
  font-size: 13px;
  color: #344054;
}

.answer-text {
  font-size: 14px;
  color: #101828;
  padding: 10px;
  background-color: #F9FAFB;
  border-radius: 4px;
  font-family: euclid_regular, sans-serif;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.answer-text.not-answered {
  color: #667085;
  font-style: italic;
}

.answer-badge {
  display: inline-block;
  padding: 6px 12px;
  background-color: #F5F5FF;
  border: 1px solid #4A68F8;
  border-radius: 6px;
  font-family: euclid_medium, sans-serif;
  font-size: 13px;
  color: #4A68F8;
}

@media (max-width: 768px) {
  .onboarding-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .header-right {
    width: 100%;
    justify-content: space-between;
  }

  .stat-badge {
    flex: 1;
  }
}
</style>
